import { send } from '@actual-app/core/platform/client/connection';
import type {
  AttachmentLibraryCursor,
  AttachmentLibraryFilters,
  AttachmentLibraryPage,
  TransactionAttachmentView,
} from '@actual-app/core/types/models';
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

function normalizeLibraryFilters(
  filters: AttachmentLibraryFilters,
): AttachmentLibraryFilters {
  return {
    view: filters.view,
    ...(filters.cursor
      ? {
          cursor: {
            timestamp: filters.cursor.timestamp,
            id: filters.cursor.id,
          },
        }
      : {}),
    ...(filters.limit === undefined ? {} : { limit: filters.limit }),
    ...(filters.search === undefined || filters.search === ''
      ? {}
      : { search: filters.search }),
    ...(filters.accountId === undefined || filters.accountId === ''
      ? {}
      : { accountId: filters.accountId }),
    ...(filters.mimeFamily === undefined
      ? {}
      : { mimeFamily: filters.mimeFamily }),
    ...(filters.attachmentDateFrom === undefined
      ? {}
      : { attachmentDateFrom: filters.attachmentDateFrom }),
    ...(filters.attachmentDateTo === undefined
      ? {}
      : { attachmentDateTo: filters.attachmentDateTo }),
    ...(filters.syncState === undefined
      ? {}
      : { syncState: filters.syncState }),
  };
}

export const attachmentQueries = {
  all: () => ['transaction-attachments'] as const,
  lists: () => [...attachmentQueries.all(), 'list'] as const,
  libraryRoot: () => [...attachmentQueries.all(), 'library'] as const,
  list: (transactionId: string) =>
    queryOptions<TransactionAttachmentView[]>({
      queryKey: [...attachmentQueries.lists(), transactionId],
      queryFn: () => send('transaction-attachments-list', { transactionId }),
      placeholderData: [],
      staleTime: Infinity,
    }),
  library: (filters: AttachmentLibraryFilters) => {
    const normalizedFilters = normalizeLibraryFilters(filters);

    return infiniteQueryOptions<AttachmentLibraryPage>({
      queryKey: [
        ...attachmentQueries.libraryRoot(),
        normalizedFilters,
      ] as const,
      queryFn: ({ pageParam }) => {
        const cursor = pageParam as AttachmentLibraryCursor | undefined;
        return send(
          'transaction-attachments-library-list',
          cursor === undefined
            ? normalizedFilters
            : { ...normalizedFilters, cursor },
        );
      },
      initialPageParam: undefined,
      getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
    });
  },
};
